import type { Editor } from '@tiptap/core';
import { Mention } from '@tiptap/extension-mention';
import { createMentionSearchHighlightNodeView } from '@narrativebench/editor-core';
import type { EditorState } from '@tiptap/pm/state';
import type {
  CreateableMentionableEntityType,
  MentionableEntity,
} from '../components/RichTextField';
import {
  buildMentionSuggestionItems,
  createMentionSuggestionRenderer,
  isMentionCreateItem,
  isMentionNoMatchItem,
  MENTION_ADD_NEW_LABEL_KEYS,
} from './mention-suggestions';

export const EntityMention = Mention.extend({
  // Leaf atoms disallow marks by default; allow highlightColor (and any other marks).
  marks: '_',
  addAttributes() {
    return {
      ...this.parent?.(),
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label') ?? element.textContent?.replace(/^@/, '') ?? null,
        renderHTML: (attributes) =>
          attributes.label != null ? { 'data-label': attributes.label } : {},
      },
      entityType: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-entity-type'),
        renderHTML: (attributes) =>
          attributes.entityType ? { 'data-entity-type': attributes.entityType } : {},
      },
      documentId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-document-id'),
        renderHTML: (attributes) =>
          attributes.documentId ? { 'data-document-id': attributes.documentId } : {},
      },
    };
  },
  addNodeView() {
    return createMentionSearchHighlightNodeView();
  },
});

export type MentionInsertNodeType = 'mention' | 'scriptMention';

export type CreateEntityMentionExtensionOptions = {
  getMentionableEntities: () => MentionableEntity[];
  getTypeLabel: (type: string) => string;
  getNoMatchLabel: () => string;
  getCreateLabel: (type: CreateableMentionableEntityType) => string;
  getCreateableTypes: (fallbackDocumentId?: string) => CreateableMentionableEntityType[];
  getCurrentDocumentId?: () => string | null | undefined;
  openCreateEntity?: (
    type: CreateableMentionableEntityType,
    name: string,
    options?: {
      documentId?: string;
      onCreated?: (entity: MentionableEntity) => void;
    },
  ) => void;
  shouldSuppressSuggestions?: () => boolean;
  shouldAllowMentions?: () => boolean;
  resolveMentionNodeType?: (entity: Pick<MentionableEntity, 'type'>) => MentionInsertNodeType;
};

export function resolveScriptAwareMentionNodeType(
  entity: Pick<MentionableEntity, 'type'>,
  isScriptModeEnabled: boolean,
): MentionInsertNodeType {
  if (isScriptModeEnabled && (entity.type === 'character' || entity.type === 'location')) {
    return 'scriptMention';
  }
  return 'mention';
}

function buildMentionInsertNodes(
  entity: Pick<MentionableEntity, 'id' | 'label' | 'type' | 'documentId'>,
  nodeType: MentionInsertNodeType,
): Array<Record<string, unknown>> {
  const attrs: Record<string, unknown> = {
    id: entity.id,
    label: entity.label,
    entityType: entity.type,
    documentId: entity.documentId ?? null,
  };
  if (nodeType === 'scriptMention') {
    attrs.mentionSuggestionChar = '@';
  }
  return [
    {
      type: nodeType,
      attrs,
    },
  ];
}

export function buildEntityMentionSuggestionOptions(options: CreateEntityMentionExtensionOptions) {
  const {
    getMentionableEntities,
    getTypeLabel,
    getNoMatchLabel,
    getCreateLabel,
    getCreateableTypes,
    getCurrentDocumentId,
    openCreateEntity,
    shouldSuppressSuggestions,
    shouldAllowMentions,
    resolveMentionNodeType,
  } = options;

  const nodeTypeFor = (entity: Pick<MentionableEntity, 'type'>): MentionInsertNodeType =>
    resolveMentionNodeType?.(entity) ?? 'mention';

  return {
    char: '@' as const,
    allowSpaces: true,
    allow: ({ state, range }: { state: EditorState; range: { from: number; to: number } }) => {
      if (shouldSuppressSuggestions?.()) {
        return false;
      }
      if (shouldAllowMentions && !shouldAllowMentions()) {
        return false;
      }
      const $from = state.doc.resolve(range.from);
      const mentionType = state.schema.nodes.mention;
      const scriptMentionType = state.schema.nodes.scriptMention;
      const canInsertMention = Boolean(
        mentionType && $from.parent.type.contentMatch.matchType(mentionType),
      );
      const canInsertScriptMention = Boolean(
        scriptMentionType && $from.parent.type.contentMatch.matchType(scriptMentionType),
      );
      return canInsertMention || canInsertScriptMention;
    },
    items: ({ query }: { query: string }) => {
      const entities = getMentionableEntities();
      const fallbackDocumentId = entities.find((entity) => entity.documentId)?.documentId;
      return buildMentionSuggestionItems(entities, query, {
        noMatchLabel: getNoMatchLabel(),
        createableTypes: getCreateableTypes(fallbackDocumentId),
        getCreateLabel,
      });
    },
    render: createMentionSuggestionRenderer(getTypeLabel, getCurrentDocumentId),
    command: ({ editor, range, props }: { editor: Editor; range: { from: number; to: number }; props: unknown }) => {
      const item = props as ReturnType<typeof buildMentionSuggestionItems>[number];
      if (isMentionNoMatchItem(item)) {
        return;
      }
      if (isMentionCreateItem(item)) {
        const entities = getMentionableEntities();
        const fallbackDocumentId = entities.find((entity) => entity.documentId)?.documentId;
        const mentionRange = { from: range.from, to: range.to };
        openCreateEntity?.(item.type, item.query, {
          documentId: fallbackDocumentId,
          onCreated: (entity) => {
            if (editor.isDestroyed) return;
            const docSize = editor.state.doc.content.size;
            if (mentionRange.from < 0 || mentionRange.to > docSize || mentionRange.from > mentionRange.to) {
              return;
            }
            const requestedType = nodeTypeFor(entity);
            const insertType =
              requestedType === 'scriptMention' && !editor.schema.nodes.scriptMention
                ? 'mention'
                : requestedType;
            editor
              .chain()
              .focus()
              .insertContentAt(mentionRange, buildMentionInsertNodes(entity, insertType))
              .run();
          },
        });
        return;
      }
      const requestedType = nodeTypeFor(item);
      const insertType =
        requestedType === 'scriptMention' && !editor.schema.nodes.scriptMention
          ? 'mention'
          : requestedType;
      editor
        .chain()
        .focus()
        .insertContentAt(range, buildMentionInsertNodes(item, insertType))
        .run();
    },
  };
}

export function createEntityMentionExtension(options: CreateEntityMentionExtensionOptions) {
  return EntityMention.configure({
    HTMLAttributes: {
      class: 'mention',
    },
    suggestion: buildEntityMentionSuggestionOptions(options),
  });
}

export { MENTION_ADD_NEW_LABEL_KEYS };
